import { observableValue, type IObservable } from '@vscode/observables';
import { createFetchClient, type ProxyApiClient } from '@hediet/simple-api/client';
import { SemanticVersion } from '@hediet/semver';
import {
	type DaemonApiType,
	type DaemonEvent,
} from '@vscode/component-explorer-cli/daemon-types';
import type { DaemonConfig } from './DaemonConfig.js';
import { runtimeVersion } from '../runtimeVersion.js';

/** Screenshot result from the daemon */
export type DaemonScreenshotResult = DaemonApiType['screenshots']['take']['TResult'];

/** Comparison result from the daemon */
export type DaemonCompareResult = DaemonApiType['screenshots']['compare']['TResult'];

/** Fixture info from the daemon */
export type DaemonFixtureInfo = DaemonApiType['fixtures']['list']['TResult'][number];

export interface DaemonSession {
	readonly name: string;
	readonly serverUrl: string;
	readonly sourceKind: 'current' | 'worktree';
}

interface CachedScreenshot {
	readonly sourceTreeId: string;
	readonly data: DaemonScreenshotResult;
}

/**
 * Client-side model for interacting with the daemon.
 * Manages source tree IDs, screenshot caching, and the event stream.
 */
export class DaemonModel {
	private readonly _sourceTreeIds = observableValue<ReadonlyMap<string, string>>(this, new Map());
	private readonly _sessions = observableValue<readonly DaemonSession[]>(this, []);
	private readonly _screenshotCache = new Map<string, CachedScreenshot>();
	private readonly _client: ProxyApiClient<DaemonApiType>;
	private _eventStreamAbort: AbortController | null = null;

	readonly sessionName: string;

	/** Observable map of sessionName → current sourceTreeId */
	readonly sourceTreeIds: IObservable<ReadonlyMap<string, string>> = this._sourceTreeIds;

	/** Observable list of sessions from the daemon */
	readonly sessions: IObservable<readonly DaemonSession[]> = this._sessions;

	constructor(readonly config: DaemonConfig) {
		this.sessionName = config.sessionName;
		this._client = createFetchClient<DaemonApiType>('/__explorer/daemon-api', {
			getHeaders: () => ({ 'X-Client-Name': 'explorer-ui' }),
		});
	}

	/**
	 * Initializes the model: fetches initial session info and starts listening to events.
	 * Call this once after construction.
	 */
	async initialize(): Promise<void> {
		this._validateProtocolVersions();
		await this._fetchSessionInfos();
		this._startEventStream();
	}

	/**
	 * Get a screenshot for a fixture in a session, using cache when available.
	 * Cache is keyed on sessionName + fixtureId and invalidated when sourceTreeId changes.
	 */
	async getScreenshot(sessionName: string, fixtureId: string): Promise<DaemonScreenshotResult> {
		const sourceTreeId = this._sourceTreeIds.get().get(sessionName);
		if (!sourceTreeId) {
			throw new Error(`No sourceTreeId known for session '${sessionName}'`);
		}

		const cacheKey = `${sessionName}:${fixtureId}`;
		const cached = this._screenshotCache.get(cacheKey);
		if (cached && cached.sourceTreeId === sourceTreeId) {
			return cached.data;
		}

		const result = await this._client.methods.screenshots.take({
			fixtureId,
			sessionName,
			sourceTreeId,
			includeImage: true,
		});

		this._screenshotCache.set(cacheKey, { sourceTreeId, data: result });
		return result;
	}

	/**
	 * Compare a fixture across two sessions. Returns match status and hashes (no images).
	 */
	async compare(fixtureId: string, baselineSessionName: string, currentSessionName: string): Promise<DaemonCompareResult> {
		const baselineSourceTreeId = this._sourceTreeIds.get().get(baselineSessionName);
		const currentSourceTreeId = this._sourceTreeIds.get().get(currentSessionName);
		if (!baselineSourceTreeId || !currentSourceTreeId) {
			throw new Error(`Missing sourceTreeId for sessions`);
		}
		return this._client.methods.screenshots.compare({
			fixtureId,
			baselineSessionName,
			baselineSourceTreeId,
			currentSessionName,
			currentSourceTreeId,
			includeImages: false,
		});
	}

	/**
	 * List fixtures from a session.
	 */
	async listFixtures(sessionName: string): Promise<readonly DaemonFixtureInfo[]> {
		const sourceTreeId = this._sourceTreeIds.get().get(sessionName);
		if (!sourceTreeId) {
			throw new Error(`No sourceTreeId known for session '${sessionName}'`);
		}
		return this._client.methods.fixtures.list({ sessionName, sourceTreeId });
	}

	/**
	 * Get the explorer URL for a different session (for session switching via full navigation).
	 */
	getSessionExplorerUrl(sessionName: string): string | undefined {
		const session = this._sessions.get().find(s => s.name === sessionName);
		if (!session) return undefined;
		return session.serverUrl + '/___explorer';
	}

	dispose(): void {
		this._eventStreamAbort?.abort();
		this._eventStreamAbort = null;
	}

	// ---------------------------------------------------------------------------
	// Private
	// ---------------------------------------------------------------------------

	private async _fetchSessionInfos(): Promise<void> {
		const sessions = await this._client.methods.sessions();
		const map = new Map<string, string>();
		const sessionList: DaemonSession[] = [];
		for (const s of sessions) {
			if (s.isLoading) {
				continue;
			}
			map.set(s.name, s.sourceTreeId);
			sessionList.push({ name: s.name, serverUrl: s.serverUrl, sourceKind: s.sourceKind });
		}
		this._sourceTreeIds.set(map, undefined);
		this._sessions.set(sessionList, undefined);
	}

	private async _startEventStream(): Promise<void> {
		this._eventStreamAbort = new AbortController();
		const signal = this._eventStreamAbort.signal;

		try {
			const stream = await this._client.methods.events();
			for await (const event of stream) {
				if (signal.aborted) break;
				this._handleEvent(event as DaemonEvent);
			}
		} catch (e) {
			if (!signal.aborted) {
				console.error('[DaemonModel] Event stream error:', e);
				// Reconnect after a delay
				setTimeout(() => {
					if (!signal.aborted) {
						this._startEventStream();
					}
				}, 2000);
			}
		}
	}

	private _handleEvent(event: DaemonEvent): void {
		if (event.type === 'source-change') {
			// Update sourceTreeId for the session
			const current = new Map(this._sourceTreeIds.get());
			current.set(event.sessionName, event.sourceTreeId);
			this._sourceTreeIds.set(current, undefined);

			// Evict screenshot cache entries for this session
			for (const key of this._screenshotCache.keys()) {
				if (key.startsWith(`${event.sessionName}:`)) {
					this._screenshotCache.delete(key);
				}
			}
		}
	}

	private _validateProtocolVersions(): void {
		const expectedDaemonApiVersion = SemanticVersion.parse('1.0.0');
		const actualDaemonApiVersion = SemanticVersion.parse(this.config.daemonApiVersion);
		if (expectedDaemonApiVersion.major !== actualDaemonApiVersion.major || expectedDaemonApiVersion.minor !== actualDaemonApiVersion.minor) {
			throw new Error(
				`Daemon API version mismatch. Explorer expects 1.0.0, daemon provides ${this.config.daemonApiVersion}. Update CLI, vite plugin, and explorer together.`,
			);
		}

		const neededByPlugin = SemanticVersion.parse(this.config.runtimeVersionNeededByPlugin);
		if (neededByPlugin.major !== runtimeVersion.major || neededByPlugin.minor !== runtimeVersion.minor) {
			throw new Error(
				`Explorer runtime version mismatch. Plugin requires ${this.config.runtimeVersionNeededByPlugin}, explorer provides ${runtimeVersion.toString()}. Reinstall dependencies to align plugin and explorer runtime versions.`,
			);
		}
	}
}
